import React, { useState, useEffect } from 'react';
import { FaHeartbeat, FaArrowUp, FaArrowDown, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import API_URL from '../config';
import DrillDown from '../DrillDown';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

function Health() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('life');
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/health`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success') {
          setData(result.data.sort((a, b) => a.year - b.year));
        } else {
          setError(result.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to connect to API');
        setLoading(false);
      });
  }, []);

  const latest = data.length > 0 ? data[data.length - 1] : null;
  const previous = data.length > 1 ? data[data.length - 2] : null;

  const METRICS = [
    {
      title: "Life Expectancy",
      value: `${latest?.life_expectancy} yrs`,
      change: previous ? `${(latest?.life_expectancy - previous?.life_expectancy).toFixed(1)} yrs vs last year` : null,
      isPositive: latest?.life_expectancy > previous?.life_expectancy,
      subtitle: `${latest?.year}`,
      metric: {
        key: 'life_expectancy',
        label: 'Life Expectancy',
        color: '#EF4444',
        unit: ' yrs',
        higherIsBetter: true,
        description: 'Average number of years a newborn is expected to live',
        info: 'Kenya\'s life expectancy has been improving steadily due to better healthcare access, improved nutrition, and reduced child mortality rates.'
      }
    },
    {
      title: "Infant Mortality",
      value: `${latest?.infant_mortality_rate}`,
      change: previous ? `${(latest?.infant_mortality_rate - previous?.infant_mortality_rate).toFixed(1)} vs last year` : null,
      isPositive: latest?.infant_mortality_rate < previous?.infant_mortality_rate,
      subtitle: "per 1,000 births",
      metric: {
        key: 'infant_mortality_rate',
        label: 'Infant Mortality Rate',
        color: '#F59E0B',
        unit: '',
        higherIsBetter: false,
        description: 'Number of deaths per 1,000 live births before age 1',
        info: 'Kenya has made significant progress in reducing infant mortality through immunization programs, improved maternal healthcare, and better nutrition.'
      }
    },
    {
      title: "Maternal Mortality",
      value: `${latest?.maternal_mortality_rate}`,
      change: previous ? `${(latest?.maternal_mortality_rate - previous?.maternal_mortality_rate).toFixed(0)} vs last year` : null,
      isPositive: latest?.maternal_mortality_rate < previous?.maternal_mortality_rate,
      subtitle: "per 100,000 births",
      metric: {
        key: 'maternal_mortality_rate',
        label: 'Maternal Mortality Rate',
        color: '#EC4899',
        unit: '',
        higherIsBetter: false,
        description: 'Number of maternal deaths per 100,000 live births',
        info: 'Maternal mortality remains a challenge in Kenya, particularly in rural areas with limited access to skilled birth attendants and emergency obstetric care.'
      }
    },
    {
      title: "Immunization",
      value: `${latest?.immunization_coverage}%`,
      change: previous ? `${(latest?.immunization_coverage - previous?.immunization_coverage).toFixed(1)}% vs last year` : null,
      isPositive: latest?.immunization_coverage > previous?.immunization_coverage,
      subtitle: "Coverage",
      metric: {
        key: 'immunization_coverage',
        label: 'Immunization Coverage',
        color: '#8B5CF6',
        unit: '%',
        higherIsBetter: true,
        description: 'Percentage of children receiving measles immunization',
        info: 'Kenya has achieved high immunization coverage through the Kenya Expanded Programme on Immunization (KEPI), protecting children from preventable diseases.'
      }
    }
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <FaSpinner className="text-5xl text-red-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading health data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <p className="text-red-600 text-xl font-semibold mb-2">Error loading data</p>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center mb-4">
              <FaHeartbeat className="text-4xl mr-4" />
              <div>
                <h1 className="text-4xl font-bold">Health Statistics</h1>
                <p className="text-red-200 mt-1">Kenya Healthcare & Wellness Indicators</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Key Metrics */}
        {latest && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {METRICS.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MetricCard
                  {...item}
                  onClick={() => setDrillDown(item.metric)}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* Chart Tabs */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Health Trends</h3>
            <div className="flex gap-2">
              {[
                { key: 'life', label: 'Life Expectancy' },
                { key: 'mortality', label: 'Mortality' },
                { key: 'immunization', label: 'Immunization' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveChart(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeChart === tab.key
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Life Expectancy Area Chart */}
          {activeChart === 'life' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="lifeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value} years`, 'Life Expectancy']}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="life_expectancy"
                    stroke="#EF4444"
                    strokeWidth={3}
                    fill="url(#lifeGradient)"
                    dot={{ fill: '#EF4444', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Life Expectancy (yrs)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Mortality Bar Chart */}
          {activeChart === 'mortality' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                  />
                  <Legend />
                  <Bar
                    dataKey="infant_mortality_rate"
                    fill="#EF4444"
                    name="Infant Mortality (per 1,000)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="maternal_mortality_rate"
                    fill="#F59E0B"
                    name="Maternal Mortality (per 100,000)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Immunization Line Chart */}
          {activeChart === 'immunization' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: 'none' }}
                    formatter={(value) => [`${value}%`, 'Immunization Coverage']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="immunization_coverage"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    dot={{ fill: '#8B5CF6', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Immunization Coverage %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </div>

        {/* Historical Data Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Historical Data</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Year</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Life Expectancy</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Infant Mortality</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Maternal Mortality</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Immunization %</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border-t hover:bg-red-50 transition cursor-pointer"
                  >
                    <td className="px-4 py-3 font-semibold text-red-600">{row.year}</td>
                    <td className="px-4 py-3">{row.life_expectancy} yrs</td>
                    <td className="px-4 py-3">{row.infant_mortality_rate}</td>
                    <td className="px-4 py-3">{row.maternal_mortality_rate}</td>
                    <td className="px-4 py-3">{row.immunization_coverage}%</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row.source}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Data Source */}
        <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Ministry of Health Kenya,
            Kenya Demographic and Health Survey (KDHS), WHO
          </p>
        </div>
      </div>

      {/* DrillDown Panel */}
      <DrillDown
        isOpen={drillDown !== null}
        onClose={() => setDrillDown(null)}
        metric={drillDown}
        data={data}
      />
    </div>
  );
}

function MetricCard({ title, value, change, isPositive, subtitle, onClick }) {
  return (
    <div
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition cursor-pointer hover:scale-105 transform"
      onClick={onClick}
    >
      <p className="text-gray-600 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <div className="flex items-center">
        {isPositive !== null && (
          isPositive ?
            <FaArrowUp className="text-green-600 mr-1" /> :
            <FaArrowDown className="text-red-600 mr-1" />
        )}
        <p className={`text-sm ${isPositive === true ? 'text-green-600' : isPositive === false ? 'text-red-600' : 'text-gray-600'}`}>
          {change}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      <p className="text-xs text-indigo-500 mt-2 font-medium">Click for details →</p>
    </div>
  );
}

export default Health;